import { useEffect } from "react";

import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import {
  selectEnvVariableState,
  selectEnvVariable,
  selectEnvVariablesArray
} from "Reducers/environment/settings/variable";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import { getVariables } from "../thunks";

import type { ProjectLevelVariable, Variable } from "@packages/client";

export function useLoadVariables({
  organizationId,
  projectId,
  environmentId,
  projectListVariables,
  variableId
}: {
  organizationId: string;
  projectId: string;
  environmentId: string;
  projectListVariables: ProjectLevelVariable[];
  variableId?: string;
}) {
  const dispatch = useAppDispatch();

  const environment = useSelectorWithUrlParams(environmentSelector);

  const envVariables = useAppSelector(selectEnvVariableState);

  const variables = useSelectorWithUrlParams(selectEnvVariablesArray);

  const envVariable = useSelectorWithUrlParams(selectEnvVariable, {
    variableId
  });
  const envTree = [
    ...useAppSelector(
      ({ environment }) =>
        environment.tree?.[organizationId]?.[projectId]?.[environmentId].path ||
        []
    ),
    environmentId
  ];

  const parentsVariables = envTree.reduce<Record<string, Variable[]>>(
    (list, env) => {
      list[env] = Object.values(
        envVariables.data?.[organizationId]?.[projectId]?.[env] ?? {}
      );

      return list;
    },
    {}
  );
  function isInactive(variable: Variable) {
    const variables = Object.values(
      envVariables.data?.[organizationId]?.[projectId]?.[environmentId] ?? {}
    ).map(envVariable => envVariable.name);

    return variables.includes(variable.name);
  }

  function isOverridingFrom(variable: Variable) {
    const projectVar = projectListVariables.find(elt => elt.id === variable.id);

    if (projectVar) {
      return "project";
    }

    if (variable?.inherited) return false;
    const parentVar = environment?.parent
      ? parentsVariables[environment.parent]?.find(
          elt => elt.id === variable.id
        )
      : undefined;

    if (
      !parentVar ||
      ![
        "value",
        "is_json",
        "is_sensitive",
        "visible_build",
        "visible_runtime",
        "is_inheritable",
        "is_enabled"
      ].some(val => parentVar[val] !== variable[val])
    )
      return false;

    return "environment";
  }

  function getOrigin(variable: Variable) {
    const origin = envTree.reverse().find(env => {
      return !parentsVariables[env].find(elt => elt.id === variable.id)
        ?.inherited;
    });
    return origin;
  }

  useEffect(() => {
    envTree.forEach(env =>
      dispatch(
        getVariables({
          organizationId,
          projectId,
          environmentId: env
        })
      )
    );
    // Reason: Adding "envTree" to the dependency array causes infinite rerendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envTree.length, dispatch, organizationId, projectId]);

  return {
    variables,
    variable: envVariable,
    envVariables,
    loading: envVariables.loading,
    errors: envVariables.errors,
    status: envVariables.status,
    getOrigin,
    isOverridingFrom,
    isInactive
  };
}
